import { useState } from 'react'
import { useSnackbar } from '@shared/ui/notification-context'
import { useTranslation } from 'react-i18next'
import {
  dashboardLanguages,
  dashboardWeekdays,
  getDashboardLocaleKey,
  normalizeDashboardLanguageValue,
} from '@features/dashboard'
import { updateDashboardProfile, type DashboardUserProfile } from '@services/firebase'
import { Card, CardAction, CardContent, CardHeader, CardTitle, Field, FieldLabel, LmsButton, LmsSelect } from '@shared/ui'
import '../profile-layout.css'

export function PreferencesSettingsCard({
  mode = 'all',
  onSaved,
  profile,
  shell = 'card',
}: {
  readonly mode?: 'all' | 'study-plan' | 'systems'
  readonly onSaved: (patch: Partial<DashboardUserProfile>) => void
  readonly profile: DashboardUserProfile
  readonly shell?: 'card' | 'plain'
}) {
  const { enqueueSnackbar } = useSnackbar()
  const { i18n, t } = useTranslation('translation')
  const { t: tFormAccount } = useTranslation('form_account')
  const [accentDefault, setAccentDefault] = useState(
    profile.accent_default ?? 'female_north_accent',
  )
  const [displayLanguage, setDisplayLanguage] = useState(
    normalizeDashboardLanguageValue(profile.language_default ?? i18n.language),
  )
  const [dailyGoals, setDailyGoals] = useState(profile.my_daily_goal ?? [])
  const [isSaving, setIsSaving] = useState(false)
  const showSystems = mode !== 'study-plan'
  const showStudyPlan = mode !== 'systems'
  const localeKey = getDashboardLocaleKey(i18n.language)
  const enabledLanguages = dashboardLanguages.filter((language) => language.enabled)

  const handleToggleDailyGoal = (value: string) => {
    setDailyGoals((previous) =>
      previous.includes(value)
        ? previous.filter((weekday) => weekday !== value)
        : [...previous, value],
    )
  }

  const handleSave = async () => {
    const patch: Partial<DashboardUserProfile> = {
      accent_default: accentDefault,
      language_default: normalizeDashboardLanguageValue(displayLanguage),
      my_daily_goal: dailyGoals,
    }

    try {
      setIsSaving(true)
      await updateDashboardProfile(patch as Record<string, unknown>)
      onSaved(patch)
      await i18n.changeLanguage(patch.language_default ?? 'en')
      enqueueSnackbar(tFormAccount('Updated successfully'), { variant: 'success' })
    } catch {
      enqueueSnackbar(tFormAccount('Updated failed'), { variant: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const getLanguageLabel = (language: (typeof dashboardLanguages)[number]) =>
    language.name[localeKey] ?? language.name.en

  const content = (
    <div className="profileCardStack">
      {showSystems ? (
        <div className="profileFormRows">
          <Field className="profileFormRow">
            <FieldLabel htmlFor="preferences-display-language">
              {t('Change display language')}
            </FieldLabel>
            <LmsSelect
              id="preferences-display-language"
              onChange={(event) => {
                setDisplayLanguage(event.target.value)
              }}
              value={displayLanguage}
            >
              {enabledLanguages.map((language) => (
                <option key={language.value} value={language.value}>
                  {getLanguageLabel(language)}
                </option>
              ))}
            </LmsSelect>
          </Field>
          <Field className="profileFormRow">
            <FieldLabel htmlFor="preferences-default-voice">
              {t('Default voice')}
            </FieldLabel>
            <LmsSelect
              id="preferences-default-voice"
              onChange={(event) => {
                setAccentDefault(event.target.value)
              }}
              value={accentDefault}
            >
              <option value="male_north_accent">{t('Male, North accent')}</option>
              <option value="female_north_accent">{t('Female, North accent')}</option>
            </LmsSelect>
          </Field>
        </div>
      ) : null}

      {showStudyPlan ? (
        <>
          <div className="profileSectionStack">
            <p className="profileStrongText">
              {t('Study plan')}
            </p>
            <div className="profileToggleGroup">
              {dashboardWeekdays.map((weekday) => {
                const isSelected = dailyGoals.includes(weekday.value)

                return (
                  <LmsButton
                    aria-pressed={isSelected}
                    aria-label={weekday.name[localeKey] ?? weekday.name.en}
                    className="min-h-11 min-w-11 rounded-full px-4"
                    data-profile-toggle="daily-goal"
                    data-selected={isSelected ? 'true' : 'false'}
                    key={weekday.value}
                    onClick={() => {
                      handleToggleDailyGoal(weekday.value)
                    }}
                    size="sm"
                    variant={isSelected ? 'secondary' : 'outline'}
                  >
                    {weekday.name[localeKey] ?? weekday.name.en}
                  </LmsButton>
                )
              })}
            </div>
          </div>
        </>
      ) : null}
    </div>
  )

  const actions = (
    <LmsButton
      className="min-h-11 px-6"
      isLoading={isSaving}
      loadingLabel={t('Saving')}
      onClick={() => void handleSave()}
      variant="primary"
    >
      {t('Save')}
    </LmsButton>
  )

  if (shell === 'plain') {
    return (
      <div className="profileCardStack">
        <div className="profileHeaderRow">
          <h2 className="profileCardTitle">
            {mode === 'study-plan' ? t('Study plan') : t('Systems')}
          </h2>
          <div className="profileHeaderAction">{actions}</div>
        </div>

        {content}

        <div className="profileActionRow profileMobileSubmit">{actions}</div>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Systems')}</CardTitle>
        <CardAction>{actions}</CardAction>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}
